import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { from, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import * as fromAuthActions from '../../../auth/state/auth.actions';
import { FileEntityKey, FileEntityKind } from '../../../shared/data-types/file-types';
import { HttpParamObject } from '../../../shared/data-types/http-types';
import { HttpOperation } from '../../enums/http-operation';
import { UserModel } from '../../models/user/user.model';
import { EndpointService } from '../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../services/http-error.service';
import * as fromFilesActions from '../files/files.actions';
import { selectFile } from '../files/files.selectors';
import * as fromUserActions from '../users/users.actions';

@Injectable()
export class UsersEffects {
  fetchUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUserActions.fetchUserList),
      map((action) => action.payload),
      switchMap((filterSelf) => {
        let params = new HttpParams();
        if (filterSelf) {
          params = params.append('filter_self', filterSelf);
        }
        const endpoint = this.endpointService.getUsersSlug(HttpOperation.Get);

        return this.http.get<unknown>(endpoint, { params }).pipe(
          map((json: unknown[]) => {
            const users = plainToInstance(UserModel, json);
            return fromUserActions.setUsers({ payload: users });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Users',
              fromUserActions.fetchUserList,
              fromUserActions,
              error,
            );
          }),
        );
      }),
    );
  });

  fetchSingleUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUserActions.fetchSingleUser),
      map((data) => data.payload),
      concatMap((userId) => {
        const endpoint = this.endpointService.getUsersSlug(HttpOperation.Get, userId);

        return this.http.get<unknown>(endpoint).pipe(
          map((json: unknown) => {
            const user = plainToInstance(UserModel, json);
            return fromUserActions.setSingleUser({ payload: user });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Users',
              fromUserActions.fetchSingleUser,
              fromUserActions,
              error,
            );
          }),
        );
      }),
    );
  });

  addUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUserActions.addUser),
      map((action) => action.payload),
      mergeMap((userModel) => {
        const endpoint = this.endpointService.getUsersSlug(HttpOperation.Post);
        return this.http.post<unknown>(endpoint, userModel).pipe(
          map((user: UserModel) => {
            return fromUserActions.userAdded({ payload: user });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Users',
              fromUserActions.addUser,
              fromUserActions,
              error,
            );
          }),
        );
      }),
    );
  });

  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUserActions.updateUser),
      map((action) => action.payload),
      mergeMap((userModel) => {
        const endpoint = this.endpointService.getUsersSlug(HttpOperation.Put, userModel.id);
        const userHttp = instanceToPlain(userModel);
        return this.http.put<UserModel>(endpoint, userHttp).pipe(
          map((user: UserModel) => {
            return fromUserActions.userUpdated({ payload: user });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Users',
              fromUserActions.updateUser,
              fromUserActions,
              error,
            );
          }),
        );
      }),
    );
  });

  deleteUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromUserActions.deleteUser),
      map((action) => action.payload),
      mergeMap((user) => {
        const endpoint = this.endpointService.getUsersSlug(HttpOperation.Delete, user.id);
        return this.http.delete<unknown>(endpoint).pipe(
          map(() => {
            return fromUserActions.userDeleted({
              payload: user,
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Users',
              fromUserActions.deleteUser,
              fromUserActions,
              error,
            );
          }),
        );
      }),
    );
  });

  loadAvatarThumb$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        fromAuthActions.setLoggedInUser,
        fromUserActions.setSingleUser,
        fromUserActions.setUsers,
      ),

      // normalize payload -> number[]
      mergeMap((action: any) => {
        const payload = action.payload;

        const userIds: number[] = Array.isArray(payload)
          ? payload.map((u: UserModel) => u.id)
          : payload?.id !== null
            ? [payload.id]
            : [];

        return from(userIds);
      }),

      // per userId, avoid refetch if already cached
      mergeMap((userId: number) =>
        of(userId).pipe(
          concatLatestFrom(() => this.store.select(selectFile(userId))),
          filter(([, fileModel]) => !fileModel),
          map(() => {
            const params: HttpParamObject = {
              kind: FileEntityKind.Avatar,
              [FileEntityKey.FileId]: userId,
              thumb: true,
            };

            return fromFilesActions.fetchSingleFile({
              payload: { params, downloadToDisk: false },
            });
          }),
        ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
    private readonly store: Store,
  ) {}
}
